// WellTable.jsx
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Stack,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  Card,
  CardContent,
  Typography,
  Chip,
  useMediaQuery,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';

function formatYyyymmdd(v) {
  if (!v) return '';
  const s = String(v).trim();
  if (!/^\d{8}$/.test(s)) return s;
  return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
}

function toFixedOrBlank(v, digits = 6) {
  if (v === undefined || v === null || Number.isNaN(v)) return '';
  return Number(v).toFixed(digits);
}

function coordPair(lat, lng) {
  if (lat == null || lng == null) return '';
  return `${toFixedOrBlank(lat)}, ${toFixedOrBlank(lng)}`;
}

function chipColorByStatus(status) {
  const s = (status || '').toUpperCase();
  if (s.includes('PUMP')) return 'success';
  if (s.includes('DRILL') || s.includes('SPUD')) return 'warning';
  if (s.includes('ABAND') || s.includes('SUSP')) return 'default';
  return 'info';
}

function chipColorByFluid(fluid) {
  const f = (fluid || '').toUpperCase();
  if (f.includes('OIL') || f.includes('CRUDE')) return 'warning';
  if (f.includes('GAS')) return 'info';
  if (f.includes('WATER')) return 'default';
  return 'default';
}

export default function WellTable({ rows = [], onOpenWell }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));

  const [q, setQ] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState('all'); // all|pumping|other

  const filtered = React.useMemo(() => {
    const query = q.trim().toLowerCase();

    return (rows || []).filter((r) => {
      const status = String(r?.status || '').toUpperCase();

      if (statusFilter === 'pumping' && !status.includes('PUMP')) return false;
      if (statusFilter === 'other' && status.includes('PUMP')) return false;

      if (!query) return true;

      const haystack = [
        r?.well_name,
        r?.operator,
        r?.jurisdiction,
        r?.uwi_formatted,
        r?.unique_identifier,
        r?.api,
        r?.api_nodash,
        r?.permit_number,
        r?.surface_location,
        r?.status,
        r?.fluid,
        r?.table_name,
        r?.lease_name,
        r?.well_number,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(query);
    });
  }, [rows, q, statusFilter]);

  const handleOpen = React.useCallback(
    (well) => {
      if (onOpenWell) onOpenWell(well);
      // Optional route pattern if you want:
      // navigate(`/app/wells/${encodeURIComponent(well.cid)}`);
      void navigate; // keep import even if you only use onOpenWell
    },
    [onOpenWell, navigate]
  );

  const columns = React.useMemo(
    () => [
      {
        field: 'uwi',
        headerName: 'UWI/API',
        flex: 1.2,
        minWidth: 220,
        valueGetter: (p) => p.row?.uwi_formatted || p.row?.unique_identifier || '',
      },
      //{
      //field: 'api',
      //headerName: 'API',
      //flex: 1,
      //minWidth: 170,
      //valueGetter: (p) => p.row?.api || p.row?.api_nodash || '',
      //},
      { field: 'lease_name', headerName: 'Lease Name', flex: 1.2, minWidth: 240 },
      { field: 'well_name', headerName: 'Well Name', flex: 0.8, minWidth: 140 },
      { field: 'well_number', headerName: 'Well Number', flex: 0.8, minWidth: 140 },
      { field: 'operator', headerName: 'Operator', flex: 1.1, minWidth: 220 },
      {
        field: 'status',
        headerName: 'Status',
        flex: 0.8,
        minWidth: 140,
        renderCell: (p) => (
          <Chip
            size="small"
            label={p.row?.status || '—'}
            color={chipColorByStatus(p.row?.status)}
            variant="outlined"
          />
        ),
        sortComparator: (a, b) => String(a || '').localeCompare(String(b || '')),
      },
      {
        field: 'fluid',
        headerName: 'Fluid',
        flex: 0.8,
        minWidth: 150,
        renderCell: (p) => (
          <Chip
            size="small"
            label={p.row?.fluid || '—'}
            color={chipColorByFluid(p.row?.fluid)}
            variant="outlined"
          />
        ),
        sortComparator: (a, b) => String(a || '').localeCompare(String(b || '')),
      },
      { field: 'jurisdiction', headerName: 'Jurisdiction', flex: 0.8, minWidth: 140 },
      { field: 'permit_number', headerName: 'Permit', flex: 0.7, minWidth: 120 },
      {
        field: 'spud_date',
        headerName: 'Spud Date',
        flex: 0.7,
        minWidth: 120,
        valueGetter: (p) => formatYyyymmdd(p.row?.spud_date),
      },
      { field: 'surface_location', headerName: 'Surface Loc', flex: 0.9, minWidth: 170 },
      {
        field: 'surface_coords',
        headerName: 'Surface Coords',
        flex: 1,
        minWidth: 210,
        valueGetter: (p) =>
          p.row?.surface_hole_coordinates || coordPair(p.row?.lat, p.row?.lng),
      },
      {
        field: 'bottom_coords',
        headerName: 'Bottom Coords',
        flex: 1,
        minWidth: 210,
        valueGetter: (p) =>
          p.row?.bottom_hole_coordinates ||
          coordPair(p.row?.bottomHoleLat, p.row?.bottomHoleLng),
      },
      { field: 'tvd', headerName: 'TVD', type: 'number', flex: 0.6, minWidth: 100 },
      //{ field: 'table_name', headerName: 'Table', flex: 0.6, minWidth: 110 },
    ],
    []
  );

  return (
    <Stack spacing={2}>
      {/* Filters */}
      {/*
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
        <TextField
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search…"
          size="small"
          fullWidth
        />

        <ToggleButtonGroup
          value={statusFilter}
          exclusive
          onChange={(_, v) => v && setStatusFilter(v)}
          size="small"
        >
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="pumping">Pumping</ToggleButton>
          <ToggleButton value="other">Other</ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      */}

      {/* Mobile: cards */}
      {!isMdUp && (
        <Stack spacing={1}>
          {filtered.map((w) => {
            const uwi = w?.uwi_formatted || w?.unique_identifier || '';
            const api = w?.api || w?.api_nodash || '';
            const surface = w?.surface_hole_coordinates || coordPair(w?.lat, w?.lng);
            const bottom =
              w?.bottom_hole_coordinates || coordPair(w?.bottomHoleLat, w?.bottomHoleLng);

            return (
              <Card
                key={w?.cid || uwi || api}
                variant="outlined"
                onClick={() => handleOpen(w)}
                sx={{ cursor: onOpenWell ? 'pointer' : 'default' }}
              >
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                    <Typography fontWeight={700} noWrap sx={{ maxWidth: '70%' }}>
                      {w?.well_name || '—'}
                    </Typography>

                    <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                      <Chip
                        size="small"
                        label={w?.status || '—'}
                        color={chipColorByStatus(w?.status)}
                        variant="outlined"
                      />
                      <Chip
                        size="small"
                        label={w?.fluid || '—'}
                        color={chipColorByFluid(w?.fluid)}
                        variant="outlined"
                      />
                    </Stack>
                  </Stack>

                  {(w?.operator || w?.jurisdiction) && (
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {[w?.operator, w?.jurisdiction].filter(Boolean).join(' • ')}
                    </Typography>
                  )}

                  {(uwi || api) && (
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {[
                        uwi && `UWI: ${uwi}`,
                        api && `API: ${api}`,
                      ]
                        .filter(Boolean)
                        .join(' • ')}
                    </Typography>
                  )}

                  {(w?.permit_number || w?.spud_date) && (
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {[
                        w?.permit_number && `Permit: ${w.permit_number}`,
                        w?.spud_date && `Spud: ${formatYyyymmdd(w.spud_date)}`,
                      ]
                        .filter(Boolean)
                        .join(' • ')}
                    </Typography>
                  )}

                  {(w?.surface_location || surface || bottom) && (
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {[
                        w?.surface_location && `Surface: ${w.surface_location}`,
                        surface && `S: ${surface}`,
                        bottom && `BH: ${bottom}`,
                      ]
                        .filter(Boolean)
                        .join(' • ')}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}

      {/* Desktop: DataGrid */}
      {isMdUp && (
        <Box sx={{ minHeight: 400, width: '100%' }}>
          <DataGrid
            rows={filtered}
            columns={columns}
            getRowId={(r) =>
              r?.unique_identifier
            }
            disableRowSelectionOnClick
            density="compact"
            pageSizeOptions={[25, 50, 100]}
            initialState={{
              pagination: { paginationModel: { pageSize: 25, page: 0 } },
              sorting: { sortModel: [{ field: 'spud_date', sort: 'desc' }] },
            }}
            onRowClick={(params) => handleOpen(params.row)}
            sx={{
              '& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus': { outline: 'none' },
            }}
          />
        </Box>
      )}
    </Stack>
  );
}